import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { PatternFormat } from 'react-number-format';

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { CheckCircle } from 'lucide-react';
import { api } from '@/lib/api/client';
import { getApiErrorMessage } from '@/lib/utils';
import { formatTime, parseTimeToSeconds } from '@/lib/utils/date';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { newTaskKeys, newTasksApi } from '../api';
import type { Task } from '../types';

const schema = z.object({
  billableDurationInSeconds: z.number().optional(),
  durationInSeconds: z.number().optional(),
}).refine(
  (v) => !v.billableDurationInSeconds || !v.durationInSeconds || v.billableDurationInSeconds <= v.durationInSeconds,
  {
    path: ['billableDurationInSeconds'],
    message: 'Billable duration cannot exceed total duration',
  }
);

type FormValues = z.infer<typeof schema>;

interface AcceptTaskDialogProps {
  task: Task;
  onSuccess?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
  disabledReason?: string;
}

export function AcceptTaskDialog({
  task,
  onSuccess,
  open: openProp,
  onOpenChange,
  hideTrigger,
  disabledReason,
}: AcceptTaskDialogProps) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;
  const setOpen = React.useCallback(
    (next: boolean) => {
      if (isControlled) {
        onOpenChange?.(next);
      } else {
        setInternalOpen(next);
      }
    },
    [isControlled, onOpenChange],
  );
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isMdPartner = user?.role?.type === 'MD';

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      billableDurationInSeconds: undefined,
      durationInSeconds: undefined,
    },
  });

  // MD role: fetch the chain's total + billable durations from the dedicated endpoint.
  const { data: durationData } = useQuery({
    queryKey: [...newTaskKeys.detail(task.id), 'durations'],
    queryFn: () => newTasksApi.getDurations(task.id),
    enabled: open && isMdPartner,
  });

  // Non-MD role: fetch activities and sum durations client-side
  const { data: activitiesData } = useQuery({
    queryKey: ['getActivitiesByTaskId', { limit: 1000, offset: 0, taskId: task.id }],
    queryFn: async () => {
      const res = await api.get<{ results: Array<{ userEnteredTimeInSeconds?: number }> }>('/activities', {
        params: { limit: 1000, offset: 0, taskId: task.id },
      });
      return res;
    },
    enabled: open && !isMdPartner,
  });

  // Total = every activity (any status); billable = ACTIVE activities only.
  // For the MD path both come from the backend; the non-MD client sum (rarely
  // used — Accept is MD-only) populates both with the same value.
  const { totalDuration, billableDuration } = React.useMemo(() => {
    if (isMdPartner) {
      return {
        totalDuration: Number(durationData?.totalDuration) || 0,
        billableDuration: Number(durationData?.billableDuration) || 0,
      };
    }
    const list = activitiesData?.results ?? [];
    const sum = list.reduce((s, a) => s + (Number(a?.userEnteredTimeInSeconds) || 0), 0);
    return { totalDuration: sum, billableDuration: sum };
  }, [isMdPartner, durationData, activitiesData]);

  // Set default values when dialog opens: Duration = total, Billable = billable.
  React.useEffect(() => {
    if (!open) return;
    form.setValue('durationInSeconds', totalDuration || 0, { shouldValidate: true });
    form.setValue('billableDurationInSeconds', billableDuration || 0, { shouldValidate: true });
  }, [open, totalDuration, billableDuration, form]);

  // Reset form when dialog closes
  React.useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  const { mutate: acceptMutation, isPending } = useMutation({
    mutationFn: (data: FormValues) => api.patch(`/tasks/accept/${task.id}`, data),
    onSuccess: () => {
      toast.success(t('taskAccepted'));
      queryClient.invalidateQueries({ queryKey: newTaskKeys.all });
      form.reset();
      setOpen(false);
      onSuccess?.();
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('failedToAcceptTask')));
    },
  });

  const onSubmit = (values: FormValues) => {
    acceptMutation(values);
  };

  const triggerButton = (
    <Button size="sm" disabled={!!disabledReason}>
      <CheckCircle className="h-4 w-4" />
      {t('approveToBillingAction')}
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!hideTrigger && (
        disabledReason ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} className="inline-flex">{triggerButton}</span>
              </TooltipTrigger>
              <TooltipContent>{disabledReason}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <DialogTrigger asChild>{triggerButton}</DialogTrigger>
        )
      )}

      <DialogContent className="!flex !flex-col sm:max-w-lg p-0 gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle className="text-xl font-semibold">
            {t('accept')} {t('task')}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t('accept')} {t('task')}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            id="accept-new-task-form"
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-4 px-6 py-4"
            noValidate
          >
            {isMdPartner && (
              <div className="flex items-center justify-between rounded-md border bg-muted/50 px-3 py-2 text-sm">
                <span className="text-muted-foreground">{t('totalRunTime')}</span>
                <span className="font-mono font-medium tabular-nums">
                  {formatTime(totalDuration)}
                </span>
              </div>
            )}

            <FormField
              control={form.control}
              name="durationInSeconds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('durationInSeconds')}</FormLabel>
                  <FormControl>
                    <PatternFormat
                      format="##:##:##"
                      value={formatTime(field.value ?? 0)}
                      onChange={(e) => {
                        const seconds = parseTimeToSeconds(e.target.value);
                        field.onChange(seconds);
                      }}
                      customInput={Input}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="billableDurationInSeconds"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('billableDurationInSeconds')}</FormLabel>
                  <FormControl>
                    <PatternFormat
                      format="##:##:##"
                      value={formatTime(field.value ?? 0)}
                      onChange={(e) => {
                        const seconds = parseTimeToSeconds(e.target.value);
                        field.onChange(seconds);
                      }}
                      customInput={Input}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </form>
        </Form>

        <DialogFooter className="px-6 py-4 border-t shrink-0">
          <DialogClose asChild>
            <Button type="button" variant="outline">
              {t('cancel')}
            </Button>
          </DialogClose>
          <Button type="submit" form="accept-new-task-form" disabled={isPending}>
            {isPending ? t('saving') : t('save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
